<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import SectionFormSelector from '@/components/SectionFormSelector.vue';
import FormAnalysisContainer from '@/components/FormAnalysisContainer.vue';
import { getTranslation } from '@/composables/data-display-utils';
import type { FormDto, Locales, SectionDto } from '@/services/types/api.types';
import { Constants } from '@/constants';

const { t } = useI18n();

const props = defineProps<{
  consultationId: number;
  languageEnabled: Locales[];
}>();

const selectedSection = ref<SectionDto | null>(null);
const selectedForm = ref<FormDto | null>(null);
const isNavigationExpanded = ref(true);

const selectionLabel = computed(() => {
  if (selectedForm.value) {
    return getTranslation(selectedForm.value.question, 'title', props.languageEnabled);
  }
  if (selectedSection.value) {
    return getTranslation(selectedSection.value, 'title', props.languageEnabled);
  }
  return null;
});

// Check if selected form is analyzable
const isSelectedFormAnalyzable = computed(() => {
  if (!selectedForm.value) return false;
  return isFormAnalyzable(selectedForm.value);
});

const onSectionSelected = (section: SectionDto) => {
  selectedSection.value = section;
  selectedForm.value = null;
};

const onFormSelected = (form: FormDto) => {
  selectedForm.value = form;
  // Close navigation when form is selected to focus on content
  isNavigationExpanded.value = false;
};

const onSectionCleared = () => {
  selectedSection.value = null;
  selectedForm.value = null;
};

const onFormCleared = () => {
  selectedForm.value = null;
};

const isFormAnalyzable = (form: FormDto): boolean => {
  return form.are_comments_enabled || form.type === Constants.Form.Type.OpenQuestion;
};

const hasFormAnalysis = (form: FormDto): boolean => {
  return form.has_ai_insights === true || form.ai_insights != null;
};
</script>
<template>
  <div class="ai-analysis column">
    <!-- Navigation Control Section -->
    <q-card flat bordered class="navigation-section q-mt-md">
      <q-expansion-item
        v-model="isNavigationExpanded"
        :label="t('consultation.ai.navigation.title')"
        icon="tune"
        header-class="bg-grey-1"
        class="navigation-expansion"
      >
        <template #header>
          <q-item-section avatar>
            <q-icon name="tune" />
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-weight-medium">
              {{ t('consultation.ai.navigation.title') }}
            </q-item-label>
          </q-item-section>
        </template>
        <q-separator />
        <q-card-section class="q-pt-md">
          <div class="filter-controls">
            <section-form-selector
              :consultation-id="props.consultationId"
              :language-enabled="props.languageEnabled"
              :section="selectedSection"
              :form="selectedForm"
              @section-selected="onSectionSelected"
              @section-cleared="onSectionCleared"
              @form-selected="onFormSelected"
              @form-cleared="onFormCleared"
            >
              <template #form-option-badges="{ form }">
                <div class="row q-gutter-xs no-wrap">
                  <q-badge v-if="isFormAnalyzable(form)" color="blue-5" text-color="white" class="analysis-badge">
                    <q-icon name="model_training" size="14px" />
                    <q-tooltip>{{ t('consultation.ai.badges.analyzable') }}</q-tooltip>
                  </q-badge>
                  <q-badge v-if="hasFormAnalysis(form)" color="green-5" text-color="white" class="analysis-badge">
                    <q-icon name="check_circle" size="14px" />
                    <q-tooltip>{{ t('consultation.ai.badges.analyzed') }}</q-tooltip>
                  </q-badge>
                </div>
              </template>
            </section-form-selector>
          </div>
        </q-card-section>
      </q-expansion-item>
    </q-card>

    <!-- Analysis container or empty state -->
    <div v-if="selectedForm" class="analysis-content q-mt-md">
      <form-analysis-container
        v-if="isSelectedFormAnalyzable"
        :form="selectedForm"
        :language-enabled="props.languageEnabled"
      />
      <q-banner v-else dense class="bg-blue-1 text-blue-9">
        <template #avatar>
          <q-icon name="info" color="blue-7" />
        </template>
        {{ t('consultation.ai.not_analyzable') }}
      </q-banner>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ai-analysis {
  .navigation-section {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    .navigation-expansion {
      :deep(.q-item) {
        padding: 12px 16px;
      }

      :deep(.q-item__section--avatar) {
        min-width: 40px;
      }

      .selected-form-chip {
        font-size: 0.875rem;
        height: 28px;
      }
    }
  }

  .filter-controls {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .analysis-content {
    width: 100%;
  }

  .analysis-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    border-radius: 4px;
  }
}
</style>
